import { z } from "zod";
import { apiClient } from "./api";

// Mirrors `MAX_ITEM_TEXT_CHARS` in `backend/src/user_memory/policy.py`. A
// standing preference is a sentence; longer text is a session summary, and the
// backend rejects it on every write path.
export const MAX_MEMORY_TEXT_LENGTH = 280;

// Mirrors `UserMemoryItemResponse` in `backend/src/api/user_memory.py`.
export const userMemoryItemSchema = z.object({
  id: z.guid(),
  text: z.string(),
  category: z.string(),
  confidence: z.number(),
  source_session_ids: z.array(z.guid()),
  user_edited: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
  // Set only on archived items; null for active ones.
  archived_at: z.string().nullable().default(null),
  archive_reason: z.string().nullable().default(null),
  // How this item reaches the model (`retrieve-user-memory-by-relevance`):
  // `always` for the block sent on every turn, `when_relevant` for a durable item
  // admitted only when the question relates to it, `never` for one that has not
  // earned durability (or when injection is off). Only the active list computes
  // it; archived responses keep the default.
  injection: z.enum(["always", "when_relevant", "never"]).default("never"),
  // Evidence (`make-user-memory-scoring-explicit`). Durability is
  // `corroboration_count >= 2 || stated || user_edited`, so this is what the
  // panel shows instead of a confidence percentage.
  corroboration_count: z.number().int().default(1),
  stated: z.boolean().default(false),
});
export type UserMemoryItem = z.infer<typeof userMemoryItemSchema>;

const userMemoryListSchema = z.array(userMemoryItemSchema);

// Mirrors `UserMemoryUnreadResponse` — drives the Settings nav badge.
export const userMemoryUnreadSchema = z.object({
  unread: z.boolean(),
  pending_suggestions: z.number().int().nonnegative(),
});

export const userMemorySuggestionSchema = z.object({
  id: z.guid(),
  target_memory_id: z.guid(),
  target_text: z.string(),
  proposed_text: z.string(),
  source_session_ids: z.array(z.guid()),
  created_at: z.string(),
});
export type UserMemorySuggestion = z.infer<typeof userMemorySuggestionSchema>;
const userMemorySuggestionListSchema = z.array(userMemorySuggestionSchema);

// Mirrors `UserMemorySeenResponse` — the watermark in effect before this visit
// advanced it; the panel badges items changed after this instant. Null when the
// panel was never opened before.
export const userMemorySeenSchema = z.object({ previous_seen_at: z.string().nullable() });

// Mirrors `UserMemorySettingsResponse` — the two per-user controls.
export const userMemorySettingsSchema = z.object({
  injection_enabled: z.boolean(),
  capture_enabled: z.boolean(),
});
export type UserMemorySettings = z.infer<typeof userMemorySettingsSchema>;

export const userMemoryApi = {
  list: () => apiClient.get("/settings/user-memory", userMemoryListSchema),
  listArchived: () => apiClient.get("/settings/user-memory/archived", userMemoryListSchema),
  create: (text: string) => apiClient.post("/settings/user-memory", userMemoryItemSchema, { text }),
  update: (id: string, text: string) =>
    apiClient.patch(`/settings/user-memory/${encodeURIComponent(id)}`, userMemoryItemSchema, {
      text,
    }),
  remove: (id: string) =>
    apiClient.delete(`/settings/user-memory/${encodeURIComponent(id)}`, z.void()),
  restore: (id: string) =>
    apiClient.post(
      `/settings/user-memory/${encodeURIComponent(id)}/restore`,
      userMemoryItemSchema,
      undefined,
    ),
  unread: () => apiClient.get("/settings/user-memory/unread", userMemoryUnreadSchema),
  listSuggestions: () =>
    apiClient.get("/settings/user-memory/suggestions", userMemorySuggestionListSchema),
  acceptSuggestion: (id: string, text?: string) =>
    apiClient.post(
      `/settings/user-memory/suggestions/${encodeURIComponent(id)}/accept`,
      userMemoryItemSchema,
      text === undefined ? {} : { text },
    ),
  dismissSuggestion: (id: string) =>
    apiClient.post(
      `/settings/user-memory/suggestions/${encodeURIComponent(id)}/dismiss`,
      z.void(),
      undefined,
    ),
  markSeen: () => apiClient.post("/settings/user-memory/seen", userMemorySeenSchema, undefined),
  settings: () => apiClient.get("/settings/user-memory/settings", userMemorySettingsSchema),
  updateSettings: (body: UserMemorySettings) =>
    apiClient.put("/settings/user-memory/settings", userMemorySettingsSchema, body),
};

export const USER_MEMORY_QUERY_KEY = ["settings", "user-memory"] as const;
export const USER_MEMORY_ARCHIVED_QUERY_KEY = ["settings", "user-memory", "archived"] as const;
export const USER_MEMORY_UNREAD_QUERY_KEY = ["settings", "user-memory", "unread"] as const;
export const USER_MEMORY_SUGGESTIONS_QUERY_KEY = [
  "settings",
  "user-memory",
  "suggestions",
] as const;
export const USER_MEMORY_SETTINGS_QUERY_KEY = ["settings", "user-memory", "settings"] as const;
